import { ComponentType, Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { ModalOptions } from "@ionic/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DateTime } from "luxon";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { useModalReconfirmModal } from "src/components/organisms/Modal/ModalReconfirm";
import { showReconfirmAlert } from "src/components/organisms/Modal/ModalReconfirm/utils";
import {
  Alert,
  Asterisk,
  Colors,
  fontMedium,
  Icon,
  TimeInput,
  useFeatureFlags,
  useModal,
} from "swing-components";

import {
  AlertError,
  ButtonScoreWrapper,
  CancelReason,
  ContentSingleColumn,
  ModalCancelCommitments,
  ModalCancelPolicy,
  ModalConfirmation,
  ModalConflicts,
  ModalSchoolFeedback,
  RequestDetailCard,
  SubDisinterestedModal,
  type CancelFormState,
  type CancelReasonType,
} from "~components";
import { createMyDocumentsUrl, createSkillBuilderTasksURL } from "~pages";
import { formatSkillBuilderOutroName } from "~skillBuilder/utils/helpers";
import {
  calcCancelPenaltyType,
  GET,
  getFeatureFlags,
  msg,
  POST,
  PUT,
  translate,
  useIsDesktop,
  type PartialCancelRequestPayload,
  type RequestData,
  type RequestDetailsErrorResponse,
} from "~utils";

import { ScorePage } from "../ScoreTemplates/ScorePage";

// TODO: find a better way to avoid the refetchInterval
// from running when running tests (Storybook)
type RequestDetails = {
  isTest?: boolean;
};

export function RequestDetails(props: RequestDetails) {
  const { isTest } = props;
  const params = useParams<{ requestId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ["fetchRequestDetails", params.requestId],
    queryFn: () =>
      GET("/api/sub/request/{requestId}", {
        pathParams: { requestId: params.requestId },
      }),
    refetchInterval: (query) => {
      // only poll for an open request
      if (!isTest && query.state.data?.data.status === "STATUS_OPEN") {
        return 30 * 1000; // time in ms - seconds * milliseconds = 30 seconds
      }
      return false;
    },
  });

  return (
    <ScorePage title={translate("PAGE_TITLE_REQUEST_DETAILS")} isLoading={isLoading} hasBack>
      <ContentSingleColumn>
        {error && (
          <div
            css={{
              margin: 16,
              "@media (min-width: 900px)": {
                margin: "0 16px",
              },
            }}
          >
            <AlertError margin="0" />
          </div>
        )}
        {data?.data && <RequestDetailsView request={data.data} queryError={error} />}
      </ContentSingleColumn>
    </ScorePage>
  );
}

type RequestDetailsViewProps = {
  request: RequestData;
  queryError: Error | null;
};

export function RequestDetailsView(props: RequestDetailsViewProps) {
  const { request } = props;
  const [apiError, setApiError] = useState<AxiosError | null>(null);
  const queryClient = useQueryClient();
  const [actionPayload, setActionPayload] = useState<ActionPayloadProps>({ action: undefined });

  const isDesktop = useIsDesktop();

  const buttonWrapperRef = useRef<HTMLDivElement>(null);
  const [bottomPadding, setBottomPadding] = useState(68); // default with 68px offset sticky button height

  const hasSpecialEducation = request.subjects?.includes("SPED");
  const hasMissingSkillBuilders =
    request.missingSkillbuilderAttrs && request.missingSkillbuilderAttrs.length > 0;
  const hasLanguageRequirement =
    request.bilingualRequirements && request.bilingualRequirements.length > 0;

  const { flags } = useFeatureFlags();
  const isDisinterestedEnabled = !request?.hasFitFeedback && flags?.subDisinterestedFeedback;

  const minutesTillStart = DateTime.fromISO(
    request?.latestAcceptableStart || request.startDate,
  ).diffNow("minute").minutes;
  const hasBeenTrimmed =
    !!flags?.lmrTrimEnabled &&
    request.action === "ACTION_SUB_CAN_SIGN_UP" &&
    !!request?.hasSingleDayTrim &&
    !!request?.latestAcceptableStart;
  const showArriveAsapAlert = hasBeenTrimmed && minutesTillStart >= 60;
  const showFinalFillAlert = hasBeenTrimmed && minutesTillStart < 60;
  const showArrivalTime =
    !!flags?.lmrTrimEnabled && request.status === "STATUS_CONFIRMED" && request?.subArrivalTime;

  // On mobile, Special Education requests, language requirements, or requests with missing skill builder attributes
  // display an alert above the fixed action button containing dynamic content. We need to
  // dynamically add additional bottom padding so page content isn't hidden underneath the alert.
  useEffect(() => {
    if (
      !isDesktop &&
      (hasSpecialEducation ||
        hasMissingSkillBuilders ||
        hasLanguageRequirement ||
        isDisinterestedEnabled) &&
      buttonWrapperRef.current
    ) {
      const frame = requestAnimationFrame(() => {
        const child = buttonWrapperRef.current?.querySelector("div");
        const height = child?.offsetHeight ?? 68;
        setBottomPadding(height);
      });

      return () => cancelAnimationFrame(frame);
    }
  }, [
    isDesktop,
    hasSpecialEducation,
    hasLanguageRequirement,
    isDisinterestedEnabled,
    hasMissingSkillBuilders,
  ]);

  const actionData =
    requestDetailsActionMapper[request.action as keyof typeof requestDetailsActionMapper];
  const isPast = request.status === "STATUS_COMPLETED";
  const hasFeedback = !!request.schoolFeedback;

  // type guards
  const isUsingComponent = "component" in actionData;
  const isUsingModalOptions = "modalOptions" in actionData;
  const isUsingButton = "button" in actionData;

  // using ref as don't want to trigger a re-render and instead
  // rely on setQueryData to re-render when the data has been updated
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataToSyncToCache = useRef<null | any>(null);
  // Note: without waiting for the onDidDismiss event, a new component
  // is rendered changing the modal content before the modal is closed
  function handleSyncData() {
    // only update data in client cache when it exists
    if (dataToSyncToCache.current) {
      queryClient.setQueryData(["fetchRequestDetails", request.id], dataToSyncToCache.current);
      dataToSyncToCache.current = null;
    }
    // remove any errors content the modal when modal is closed
    setApiError(null);
  }

  /********* Mutations ********/
  const { mutate: onAction, isPending: isOnActionPending } = useMutation({
    // TODO: @KoltonG - How can we help type this payload?
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (payload: any) => {
      return PUT("/api/sub/request/{requestId}", payload, {
        pathParams: { requestId: request.id },
      });
    },
    onSuccess: async (updatedRequest, payload) => {
      // set data to sync to client cache when we are ready (after modal has finished closing via onDidDismiss)
      dataToSyncToCache.current = updatedRequest;
      setActionPayload(payload);
      if (
        payload.action === "RECONFIRM" ||
        updatedRequest.data.action === "ACTION_SUB_CAN_JOIN_WAITLIST" ||
        updatedRequest.data.action === "ACTION_SUB_CAN_LEAVE_WAITLIST"
      ) {
        handleSyncData();
      }
      closeModal();
    },
    onError: (error: AxiosError) => setApiError(error),
  });

  const { mutate: partialCancelRequest, isPending: isPartialCancelPending } = useMutation<
    { data: RequestData },
    AxiosError,
    PartialCancelRequestPayload
  >({
    mutationFn: (cancelPayload: PartialCancelRequestPayload) => {
      return POST("/api/sub/request/{requestId}/partial-cancel", cancelPayload, {
        pathParams: { requestId: request.id },
      });
    },
    onSuccess: (updatedRequest) => {
      // set data to sync to client cache when we are ready (after modal has finished closing via onDidDismiss)
      dataToSyncToCache.current = updatedRequest;

      // Set the action payload to partial cancel
      // so that the alert box shows the correct message
      setActionPayload({ action: "PARTIAL_CANCEL" });

      closeModal();
    },
    onError: (error: AxiosError) => setApiError(error),
  });

  // Helper to generate a type guard for a specific modal component.
  // Helps TypeScript infer which modal is being rendered.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function makeModalTypeGuard<T extends ComponentType<any>>(modalComponent: T) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (component: ComponentType<any>): component is T => component === modalComponent;
  }

  const [arrivalTime, setArrivalTime] = useState<DateTime | undefined>(undefined);

  const isModalConfirmation = makeModalTypeGuard(ModalConfirmation);
  const isModalCancelCommitments = makeModalTypeGuard(ModalCancelCommitments);
  const isModalConflicts = makeModalTypeGuard(ModalConflicts);
  const isRequirementMissing =
    request.hasMissingRequirements?.credentialMissing ||
    request.hasMissingRequirements?.mandatoryRequirementsMissing;
  const { openReconfirmModal, closeReconfirmModal, reconfirm, shouldShowReconfirmModal } =
    useModalReconfirmModal({
      request,
      onConfirm: (payload: "RECONFIRM" | "CANCEL") => {
        if (payload === "RECONFIRM") {
          onAction({ action: "RECONFIRM" });
        } else {
          openModal();
        }
        closeReconfirmModal();
      },
      onDismiss: () => {
        closeReconfirmModal();
      },
    });

  const { openModal, closeModal } = useModal({
    component:
      isPast && !hasFeedback ? (
        <ModalSchoolFeedback
          step="rating"
          schoolName={request.school.name}
          requestId={request.id}
          onDismiss={() => closeModal()}
        />
      ) : !isUsingComponent ? (
        <></>
      ) : isModalConfirmation(actionData.component.element) ? (
        <ModalConfirmation
          {...(actionData.component.props as InferComponentProps<typeof ModalConfirmation>)}
          error={apiError}
          onConfirm={async () =>
            onAction({
              action: actionData.payload.action,
              acceptMode: acceptMode,
              ...(flags?.lmrTrimEnabled && { arrivalTime }),
            })
          }
          onDismiss={() => closeModal()}
          confirmDisabled={
            !!flags?.lmrTrimEnabled && request?.hasSingleDayTrim && arrivalTime === undefined
          }
        >
          {hasLanguageRequirement && actionData.payload.action === "SIGN_UP" && (
            <div css={{ marginBottom: 16 }}>
              <LanguageRequirementAlert languages={request.bilingualRequirements!} />
            </div>
          )}
          {actionData.payload.action === "JOIN_WAITLIST" ? (
            <ModalJoinWaitlistContent
              displayDate={request.displayDate}
              schoolName={request.school.name}
            />
          ) : (
            <ModalConfirmationContent
              displayDate={request.displayDate}
              isMultiDay={request.isMultiDay}
              schoolName={request.school.name}
              startDate={request.startDate}
              collectArrivalTime={hasBeenTrimmed}
              arrivalTime={arrivalTime}
              setArrivalTime={setArrivalTime}
              latestAcceptableStart={request?.latestAcceptableStart}
              shownAlert={
                showArriveAsapAlert ? (
                  <ArriveASAPAlert />
                ) : showFinalFillAlert ? (
                  <FinalFillAlert
                    time={DateTime.fromISO(request?.latestAcceptableStart || "").toFormat("h:mm a")}
                  />
                ) : undefined
              }
            />
          )}
        </ModalConfirmation>
      ) : isModalCancelCommitments(actionData.component.element) ? (
        <ModalCancelCommitments
          hasApiError={!!apiError}
          onConfirm={async (payload: CancelFormState | PartialCancelRequestPayload) => {
            // If the payload has a partialCancelText,
            // we need to call the partialCancelRequest mutation
            // otherwise, we need to call the onAction mutation for full cancel
            if (payload.partialCancelText) {
              partialCancelRequest(payload as PartialCancelRequestPayload);
            } else {
              onAction({ ...payload, action: actionData.payload.action });
            }
          }}
          onDismiss={() => closeModal()}
          // @ts-expect-error Type 'undefined' is not assignable to type 'CancelPenaltyType'
          penaltyType={
            request.fillDate
              ? calcCancelPenaltyType({
                  reqStartTime: request.startDate,
                  reqFillTime: request.fillDate,
                  reqCancelTime: DateTime.now().setZone("UTC"),
                })
              : undefined
          }
          step={
            // TODO: Remove FF check once the feature is stable
            getFeatureFlags().PARTIAL_CANCEL &&
            request.status === "STATUS_CONFIRMED" &&
            request.isMultiDay &&
            // Check if the request has more than one interval remaining
            hasAtLeastTwoFutureIntervals(request.intervals) &&
            // Check if the current time is after the start of the first
            // interval (i.e., the request has already started)
            DateTime.now().setZone("utc") >
              DateTime.fromISO(request.intervals[0].startDate, { zone: "utc" })
              ? "cancellation-type"
              : "cancellation-reason"
          }
          intervals={request.intervals}
          isOnConfirmPending={isPartialCancelPending || isOnActionPending}
        />
      ) : isModalConflicts(actionData.component.element) ? (
        <ModalConflicts
          {...(actionData.component.props as InferComponentProps<typeof ModalConflicts>)}
          conflicts={request.conflicts}
          hasApiError={!!apiError}
          message={translate("MODAL_CONFLICTING_COMMITMENTS_MESSAGE", {
            schoolName: request.school.name,
            startDate: DateTime.fromISO(request.startDate).toFormat("LLL dd"),
            endDate: DateTime.fromISO(request.endDate).toFormat("LLL dd"),
          })}
          onConfirm={async () => onAction({ action: actionData.payload.action })}
          onDismiss={() => closeModal()}
        />
      ) : (
        // when there is no modal to render
        <></>
      ),
    modalOptions: isUsingModalOptions
      ? { ...actionData.modalOptions, onDidDismiss: handleSyncData }
      : isPast && !hasFeedback
        ? { cssClass: "modal-sub-profile-edit" }
        : { onDidDismiss: handleSyncData },
  });

  const errResponseData = apiError?.response?.data as RequestDetailsErrorResponse;

  useEffect(() => {
    if (shouldShowReconfirmModal) {
      openReconfirmModal();
    }
  }, [shouldShowReconfirmModal, openReconfirmModal]);

  const shouldSkillBuilderDisableCTA = Boolean(
    (actionData.payload.action === "SIGN_UP" || actionData.payload.action === "JOIN_WAITLIST") &&
      hasMissingSkillBuilders,
  );

  const { openModal: openDisinterestedModal, closeModal: closeDisinterestedModal } = useModal({
    component: (
      <SubDisinterestedModal onDismiss={() => closeDisinterestedModal()} requestId={request.id} />
    ),
    modalOptions: {
      cssClass: isDesktop ? undefined : "action-sheet-modal",
      breakpoints: isDesktop ? undefined : [0, 1],
      initialBreakpoint: isDesktop ? undefined : 1,
      expandToScroll: true,
    },
  });

  return (
    <>
      <div css={{ maxWidth: 900 }}>
        {/* Show the alert box outside of the card on desktop */}
        {isDesktop && (
          // Add extra margin to the top of the alert box if there is
          // an error derived from the query at the top of the page
          <div css={{ margin: `${props.queryError ? 16 : 0}px 16px 16px` }}>
            {showReconfirmAlert(request, reconfirm) ? (
              <Alert color="success" title={translate("RECONFIRM_ALERT_TITLE")} showIcon />
            ) : (
              <RequestDetailsAlert actionPayload={actionPayload} events={request.events} />
            )}
          </div>
        )}
        <div
          css={{
            padding: `0 0 ${bottomPadding}px`,
            backgroundColor: Colors.white200,
            "@media (min-width: 900px)": { padding: 0 },
          }}
        >
          {showArriveAsapAlert && (
            <div css={{ padding: 16 }}>
              <ArriveASAPAlert />
            </div>
          )}
          {showFinalFillAlert && (
            <div css={{ padding: 16 }}>
              <FinalFillAlert
                time={DateTime.fromISO(request?.latestAcceptableStart || "").toFormat("h:mm a")}
              />
            </div>
          )}
          {showArrivalTime && (
            <div css={{ padding: 16 }}>
              <ExpectedArrivalAlert
                time={DateTime.fromISO(request.subArrivalTime || "").toFormat("h:mm a")}
              />
            </div>
          )}
          {errResponseData &&
            (errResponseData?.detail?.exception === "request-unavailable" ? (
              <div
                css={{
                  margin: 16,
                  "@media (min-width: 900px)": { margin: "0 16px" },
                }}
              >
                <Alert
                  color="warning"
                  title={translate("UNAVAILABLE_REQUEST_ALERT_TITLE")}
                  showIcon
                >
                  {translate("LABEL_UNAVAILABLE_REQUEST")}
                </Alert>
              </div>
            ) : errResponseData.error && !isUsingComponent ? (
              // Prevent this error from showing on the page when
              // it's already handled in a modal
              <div
                css={{
                  margin: 16,
                  "@media (min-width: 900px)": { margin: "0 16px" },
                }}
              >
                <AlertError margin="0" />
              </div>
            ) : null)}
          <RequestDetailCard
            request={request}
            alert={
              showReconfirmAlert(request, reconfirm) ? (
                <Alert color="success" title={translate("RECONFIRM_ALERT_TITLE")} showIcon />
              ) : (
                <RequestDetailsAlert actionPayload={actionPayload} events={request.events} />
              )
            }
          />
          {isRequirementMissing && (
            <RequirementsMissingBanner requirements={request.hasMissingRequirements} />
          )}
        </div>
        {isUsingButton && actionData.payload && (
          <div ref={buttonWrapperRef}>
            <ButtonScoreWrapper
              buttonPrimary={{
                label: actionData.button.label,
                expand: "block",
                onClick: isUsingComponent ? openModal : () => onAction(actionData.payload),
                fill: actionData.button.variant,
                disabled:
                  (actionData.payload.action === "CANCEL" && !request.fillDate) ||
                  shouldSkillBuilderDisableCTA ||
                  isRequirementMissing,
              }}
              buttonSecondary={
                isDisinterestedEnabled
                  ? {
                      label: translate("SUB_DISINTERESTED_BUTTON_LABEL"),
                      onClick: openDisinterestedModal,
                    }
                  : undefined
              }
              alert={
                // On mobile, show alerts in priority order: missing skill builders > language requirement > special education
                // On desktop, display logic is handled in the RequestDetailCard above the cancellation policy
                !isDesktop && request.status === "STATUS_OPEN" ? (
                  hasMissingSkillBuilders ? (
                    <MissingSkillBuilderAlert
                      missingSkillBuilderAttrs={request.missingSkillbuilderAttrs}
                    />
                  ) : hasLanguageRequirement ? (
                    <LanguageRequirementAlert languages={request.bilingualRequirements!} />
                  ) : (
                    hasSpecialEducation && <SpecialEducationInfoAlert />
                  )
                ) : null
              }
              backgroundColor="white200"
            />
          </div>
        )}
        {isPast && !hasFeedback && (
          <ButtonScoreWrapper
            buttonPrimary={{
              label: translate("SCHOOL_FEEDBACK_TITLE"),
              expand: "block",
              onClick: openModal,
              fill: "solid",
            }}
            backgroundColor="white200"
          />
        )}
      </div>
    </>
  );
}

type ActionPayloadProps = {
  action?: Action;
  reason?: CancelReasonType;
};

type RequestDetailsAlertProps = {
  actionPayload: ActionPayloadProps;
  events?: RequestData["events"];
};

function RequirementsMissingBanner({
  requirements,
}: {
  requirements: RequestDetailsViewProps["request"]["hasMissingRequirements"];
}) {
  const description = [
    "Please check ",
    <Link key="my-docs-link" to={createMyDocumentsUrl()}>
      My Documents.
    </Link>,
  ];

  const title =
    requirements?.credentialMissing && !requirements.mandatoryRequirementsMissing
      ? msg("MISSING_CREDENTIAL_LABEL")
      : msg("EXPIRING_DOCUMENTS_MULTIPLE_REQUIREMENTS_LABEL");

  return (
    <>
      <div css={{ marginTop: 8, marginLeft: 8 }}>
        <Icon name="Alert" color={Colors.red500} />
      </div>
      <div css={{ color: Colors.black, fontWeight: "var(--swing-font-semibold)", marginTop: 8 }}>
        {title}
        <div css={{ color: Colors.black, fontWeight: "var(--swing-font-normal)", marginTop: 8 }}>
          {description}
        </div>
      </div>
    </>
  );
}

function RequestDetailsAlert(props: RequestDetailsAlertProps) {
  const { actionPayload, events } = props;

  // Handle full cancel alert
  if (actionPayload.action === "CANCEL") {
    return (
      <Alert color="info" title={translate("CANCEL_ALERT_TITLE")} showIcon>
        {/* covering case where reason is optional for the alert props */}
        {translate("CANCEL_ALERT_MESSAGE")}:{" "}
        {CancelReason[actionPayload.reason || "CANCEL_REASON_OTHER"]}
      </Alert>
    );
  }

  // Handle partial cancel alert based on events
  if (events && events.length > 0) {
    const partialCancelEvents = events.filter(
      // make sure TS knows that the event is not null
      // to avoid the no matching index signature for type 'number' error
      (event: NonNullable<RequestData["events"]>[number]) =>
        event.eventType === "event.type/sub-req-cancel-partial-submitted" ||
        event.eventType === "event.type/sub-req-cancel-partial-confirmed",
    );

    const lastPartialEvent = partialCancelEvents[0];
    if (!lastPartialEvent) return null;

    // Events are ordered in the BE so we can just check the last one
    // and that's the most recent event
    const isProcessing =
      lastPartialEvent.eventType === "event.type/sub-req-cancel-partial-submitted";

    return (
      <Alert
        color={isProcessing ? "info" : "success"}
        title={translate("CANCEL_COMMITMENT_MODAL_PROCESSING_ALERT_TITLE", { isProcessing })}
        showIcon
      >
        {translate("CANCEL_COMMITMENT_MODAL_PROCESSING_ALERT_MESSAGE", { isProcessing })}
      </Alert>
    );
  }

  return null;
}

type MissingSkillBuilderAlertProps = {
  missingSkillBuilderAttrs: RequestData["missingSkillbuilderAttrs"];
};
export function MissingSkillBuilderAlert(props: MissingSkillBuilderAlertProps) {
  const { missingSkillBuilderAttrs } = props;
  if (!missingSkillBuilderAttrs) return null;

  return (
    <Alert color="warning" showIcon>
      <div css={{ maxHeight: 240, overflowY: "auto" }}>
        {missingSkillBuilderAttrs.length === 1 ? (
          <p css={{ margin: 0 }}>
            This school requires completion of{" "}
            <span css={fontMedium({ fontWeight: 600 })}>
              {formatSkillBuilderOutroName(missingSkillBuilderAttrs[0])}
            </span>
            . Complete in <Link to={createSkillBuilderTasksURL("swing")}>Skill Builder</Link> to
            unlock.
          </p>
        ) : (
          <>
            <p css={{ margin: 0 }}> This school requires completion of</p>
            <ul css={{ margin: 0 }}>
              {missingSkillBuilderAttrs.map((msba) => {
                return <li key={msba}>{formatSkillBuilderOutroName(msba)}</li>;
              })}
            </ul>
            <p css={{ margin: 0 }}>
              Complete in <Link to={createSkillBuilderTasksURL("swing")}>Skill Builder</Link> to
              unlock.
            </p>
          </>
        )}
      </div>
    </Alert>
  );
}

export function SpecialEducationInfoAlert() {
  return (
    <Alert color="info" title={translate("SPECIAL_EDUCATION_INFO_ALERT_TITLE")} showIcon>
      {translate("SPECIAL_EDUCATION_INFO_ALERT_MESSAGE")}
    </Alert>
  );
}

type LanguageRequirementAlertProps = {
  languages: string[];
};

export function LanguageRequirementAlert(props: LanguageRequirementAlertProps) {
  const { languages } = props;

  function formatLanguages(langs: string[]): string {
    if (langs.length === 0) return "";
    if (langs.length === 1) return langs[0];
    if (langs.length === 2) return `${langs[0]} and ${langs[1]}`;
    return `${langs.slice(0, -1).join(", ")}, and ${langs[langs.length - 1]}`;
  }

  const formattedLanguages = formatLanguages(languages);

  return (
    <Alert
      color="info"
      showIcon
      css={{
        border: "1px solid #BB88C9",
        background: "#F8F3FA",
      }}
    >
      {translate("LANGUAGE_REQUIREMENT_ALERT_PREFIX")}
      {"\u00A0"}
      <strong>{formattedLanguages}</strong>
    </Alert>
  );
}

// extract props from component
type InferComponentProps<T> = T extends ComponentType<infer P> ? P : never;

type ComponentConfig<C> = {
  component: {
    element: C;
    props: Partial<InferComponentProps<C>>;
  };
};

type ModalOptionsProps = {
  modalOptions: Omit<ModalOptions, "component" | "componentProps">;
};

type ButtonProps = {
  button: {
    label: string;
    variant: "solid" | "outline" | "clear";
  };
};

type Action =
  | "SIGN_UP"
  | "CANCEL"
  | "CONFIRM"
  | "LEAVE_WAITLIST"
  | "JOIN_WAITLIST"
  | "NOOP"
  | "PARTIAL_CANCEL";
type AcceptMode = "web" | "mobile";

type ActionMapperProps<T> = ComponentConfig<T> &
  ModalOptionsProps &
  ButtonProps & {
    payload?: { action: Action; acceptMode?: AcceptMode };
  };

const isNative = Capacitor.isNativePlatform();
const acceptMode: AcceptMode = isNative ? "mobile" : "web";

const requestDetailsActionMapper = {
  ["ACTION_SUB_CAN_SIGN_UP"]: {
    button: {
      label: translate("LABEL_SIGN_UP"),
      variant: "solid",
    },
    component: {
      element: ModalConfirmation,
      props: {
        title: translate("MODAL_CONFIRMATION_SUB_SIGN_UP_TITLE"),
        primaryButtonText: translate("MODAL_CONFIRMATION_SUB_SIGN_UP_PRIMARY_BUTTON_TEXT"),
        secondaryButtonText: translate("MODAL_CONFIRMATION_SUB_SIGN_UP_SECONDARY_BUTTON_TEXT"),
      },
    },
    modalOptions: { cssClass: "modal-confirmation" },
    payload: { action: "SIGN_UP", acceptMode },
  } satisfies ActionMapperProps<typeof ModalConfirmation>,
  ["ACTION_SUB_CAN_CANCEL"]: {
    button: {
      label: translate("LABEL_CANCEL"),
      variant: "outline",
    },
    component: {
      element: ModalCancelCommitments,
      props: {},
    },
    modalOptions: { cssClass: "modal-cancel" },
    payload: { action: "CANCEL" },
  } satisfies ActionMapperProps<typeof ModalCancelCommitments>,
  ["ACTION_SUB_CAN_CONFIRM"]: {
    button: {
      label: translate("LABEL_CAN_CONFIRM"),
      variant: "solid",
    },
    component: {
      element: ModalConflicts,
      props: {
        title: translate("MODAL_CONFLICTING_COMMITMENTS_TITLE"),
      },
    },
    modalOptions: { cssClass: "modal-conflicts" },
    payload: { action: "CONFIRM" },
  } satisfies ActionMapperProps<typeof ModalConflicts>,
  ["ACTION_SUB_CAN_JOIN_WAITLIST"]: {
    button: {
      label: translate("LABEL_JOIN_WAITLIST"),
      variant: "solid",
    },
    component: {
      element: ModalConfirmation,
      props: {
        title: translate("MODAL_JOIN_WAITLIST_TITLE"),
        primaryButtonText: translate("MODAL_JOIN_WAITLIST_PRIMARY_BUTTON_TEXT"),
        secondaryButtonText: translate("MODAL_JOIN_WAITLIST_SECONDARY_BUTTON_TEXT"),
      },
    },
    modalOptions: { cssClass: "modal-confirmation" },
    payload: { action: "JOIN_WAITLIST", acceptMode },
  } satisfies ActionMapperProps<typeof ModalConfirmation>,
  ["ACTION_SUB_CAN_LEAVE_WAITLIST"]: {
    button: {
      label: translate("LABEL_LEAVE_WAITLIST"),
      variant: "outline",
    },
    payload: { action: "LEAVE_WAITLIST" },
  } satisfies ButtonProps & { payload: { action: Action } },
  ["ACTION_SUB_CAN_NOOP"]: {
    payload: { action: "NOOP" },
  } satisfies { payload: { action: Action } },
} as const;

type ModalConfirmationContentProps = {
  displayDate: RequestData["displayDate"];
  isMultiDay: RequestData["isMultiDay"];
  schoolName: RequestData["school"]["name"];
  startDate: RequestData["startDate"];
  collectArrivalTime: boolean;
  arrivalTime: DateTime | undefined;
  setArrivalTime: Dispatch<SetStateAction<DateTime | undefined>>;
  latestAcceptableStart?: string;
  shownAlert: any;
};

export function ModalConfirmationContent(props: ModalConfirmationContentProps) {
  const {
    displayDate,
    isMultiDay,
    schoolName,
    startDate,
    arrivalTime,
    setArrivalTime,
    collectArrivalTime,
    latestAcceptableStart,
    shownAlert,
  } = props;
  const penaltyType = calcCancelPenaltyType({
    reqStartTime: startDate,
    reqFillTime: DateTime.now().setZone("UTC").minus({ minutes: 31 }), //Set to >30 to bypass grace period calculation
    reqCancelTime: DateTime.now().setZone("UTC"),
  });
  const insideCancelWindow = penaltyType !== "NO_PENALTY"; // A true value signals the request begins within 24hours.
  const [showErr, setShowErr] = useState(false);

  const { openModal: openCancelPolicyModal, closeModal: closeCancelPolicyModal } = useModal({
    component: <ModalCancelPolicy onDismiss={() => closeCancelPolicyModal()} />,
    modalOptions: {
      cssClass: `cancel-policy-modal`,
    },
  });

  const start = DateTime.fromISO(startDate);

  const onArrivalTimeChange = (e: DateTime) => {
    const newTime = start.set({ hour: e.hour, minute: e.minute });
    if (!latestAcceptableStart) {
      setArrivalTime(newTime);
      return;
    }
    const latestAcceptableStartDT = DateTime.fromISO(latestAcceptableStart);
    if (newTime > latestAcceptableStartDT) {
      setShowErr(true);
      setArrivalTime(undefined);
      return;
    }

    setShowErr(false);
    setArrivalTime(newTime);
  };

  return (
    <div>
      {shownAlert}
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "24px 1fr",
          alignItems: "flex-start",
          gap: "16px",
          marginTop: 16,
        }}
      >
        <Icon name="School" />
        <p>{schoolName}</p>
        <Icon name={`${isMultiDay ? "Calendar Overlap" : "Calendar"}`} />
        <p>{displayDate}</p>
        <Icon name="Block" />
        <div>
          {insideCancelWindow
            ? translate("CANCEL_POLICY_WARNING_PENALTY")
            : translate("CANCEL_POLICY_WARNING_NO_PENALTY")}
          <span
            onClick={openCancelPolicyModal}
            css={{ color: Colors.blue500, textDecoration: "underline", cursor: "pointer" }}
          >
            {translate("CANCEL_POLICY_LABEL")}.
          </span>
        </div>
      </div>
      <p css={{ paddingTop: 16 }}>
        {schoolName} {translate("MODAL_CONFIRMATION_SUB_SIGN_UP_MESSAGE")}.
      </p>
      {collectArrivalTime && (
        <div css={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
          <div>
            <span css={{ fontWeight: "bold" }}>{translate("EXPECTED_ETA_HEADER")}</span>{" "}
            <Asterisk />
            <br />
            <span css={{ fontSize: 14 }}>{translate("EXPECTED_ETA_SUB")}</span>
            <br />
            {showErr && latestAcceptableStart && (
              <span css={{ color: "red" }}>
                {translate("LATEST_ACCEPTABLE_ERROR", {
                  time: DateTime.fromISO(latestAcceptableStart).toFormat("h:mm a"),
                })}
              </span>
            )}
          </div>
          <TimeInput value={arrivalTime} onChange={onArrivalTimeChange} />
        </div>
      )}
    </div>
  );
}

type ModalJoinWaitlistProps = {
  displayDate: RequestData["displayDate"];
  schoolName: RequestData["school"]["name"];
};

export function ModalJoinWaitlistContent(props: ModalJoinWaitlistProps) {
  const { displayDate, schoolName } = props;
  return (
    <div>
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "24px 1fr",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <Icon name="School" />
        <p>{schoolName}</p>
        <Icon name="Calendar Overlap" />
        <p>{displayDate}</p>
        <Icon name="Flag" />
        <strong>{translate("MODAL_JOIN_WAITLIST_POLICY")}</strong>
      </div>
      <ul css={{ margin: 0 }}>
        {translate("MODAL_JOIN_WAITLIST_BEFORE")}
        <li css={{ marginLeft: 16 }}>{translate("MODAL_JOIN_WAITLIST_REMOVE")}</li>
      </ul>
      <ul css={{ marginTop: 0 }}>
        {translate("MODAL_JOIN_WAITLIST_AFTER")}
        <li css={{ marginLeft: 16 }}>
          {translate("IF_YOU")}
          <strong>{translate("DO_NOT")}</strong>
          {translate("MODAL_JOIN_WAITLIST_NO_CONFLICTING")}
        </li>
        <li css={{ marginLeft: 16 }}>{translate("MODAL_JOIN_WAITLIST_CONFLICTING")}</li>
      </ul>
    </div>
  );
}

/**
 * Checks if there are at least two future intervals remaining.
 *
 * This function is used to determine whether a request contains
 * two or more intervals that start after the current UTC time.
 * Useful in scenarios where partial cancellation is only allowed
 * if more than one (at least two) future intervals exist.
 *
 * @param intervals - An array of request intervals.
 * @returns `true` if at least two intervals start in the future, otherwise `false`.
 */

export function hasAtLeastTwoFutureIntervals(intervals: RequestData["intervals"]) {
  const now = DateTime.now().setZone("utc");

  const futureIntervals = intervals.filter((interval) => {
    const start = DateTime.fromISO(interval.startDate, { zone: "utc" });
    return start > now;
  });

  return futureIntervals.length >= 2;
}

function ArriveASAPAlert() {
  return (
    <Alert color="disclaimer" title={translate("ARRIVE_ASAP_HEADER")}>
      <span>{translate("ARRIVE_ASAP_BODY")}</span>
    </Alert>
  );
}

function FinalFillAlert({ time }: { time: string }) {
  return (
    <Alert color="disclaimer" title={translate("FINAL_FILL_HEADER", { time })}>
      <span>{translate("FINAL_FILL_BODY", { time })}</span>
    </Alert>
  );
}

function ExpectedArrivalAlert({ time }: { time: string }) {
  return (
    <Alert color="disclaimer">
      <span>{translate("EXPECTED_ARRIVAL_TIME")}&nbsp;</span>
      <span css={{ fontWeight: "bold" }}>{time}</span>
    </Alert>
  );
}
